// keyrd_console/src/api/pilotAdmin.js
import { buildKeyrdHeaders } from "./headers";

const DEFAULT_BASE_URL = "http://localhost:8000";

function trimBaseUrl(baseUrl) {
  return (baseUrl || DEFAULT_BASE_URL).replace(/\/+$/, "");
}

function buildAuthHeaders(auth = {}, contentType = null) {
  const { token, registryVersion, registryHash } = auth;

  return buildKeyrdHeaders({
    token,
    registryVersion,
    registryHash,
    contentType,
    extra: { Accept: "application/json" },
  });
}

async function readJson(res, label) {
  const text = await res.text();
  let json = null;

  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(`${label} returned non-JSON (${res.status}).`);
  }

  if (!res.ok) {
    const msg =
      json?.error?.message ||
      json?.error ||
      json?.reason ||
      `HTTP ${res.status}`;
    throw new Error(`${label} failed: ${msg}`);
  }

  return json;
}

async function getJson(path, auth, label) {
  const baseUrl = trimBaseUrl(auth?.baseUrl);
  const res = await fetch(`${baseUrl}${path}`, {
    method: "GET",
    headers: buildAuthHeaders(auth),
  });
  return readJson(res, label);
}

async function postJson(path, auth, body, label) {
  const baseUrl = trimBaseUrl(auth?.baseUrl);
  const res = await fetch(`${baseUrl}${path}`, {
    method: "POST",
    headers: buildAuthHeaders(auth, "application/json"),
    body: JSON.stringify(body || {}),
  });
  return readJson(res, label);
}

export async function getPilotOverview(auth) {
  return getJson("/console/pilot/overview", auth, "Pilot overview");
}

export async function getPilotParticipants(auth) {
  return getJson("/console/pilot/participants", auth, "Pilot participants");
}

export async function getPilotParticipantDetail(participantId, auth) {
  if (!participantId) {
    throw new Error("Pilot participant detail requires participantId.");
  }
  return getJson(
    `/console/pilot/participants/${encodeURIComponent(participantId)}`,
    auth,
    "Pilot participant detail"
  );
}

export async function getPilotPreflightList(auth) {
  return getJson("/console/pilot/preflight", auth, "Pilot preflight list");
}

export async function getPilotPreflightDetail(participantId, auth) {
  if (!participantId) {
    throw new Error("Pilot preflight detail requires participantId.");
  }
  return getJson(
    `/console/pilot/preflight/${encodeURIComponent(participantId)}`,
    auth,
    "Pilot preflight detail"
  );
}

export async function submitPilotPreflightReview(participantId, review, auth) {
  if (!participantId) {
    throw new Error("Pilot preflight review requires participantId.");
  }

  return postJson(
    `/console/pilot/preflight/${encodeURIComponent(participantId)}/review`,
    auth,
    review,
    "Pilot preflight review"
  );
}

export async function getPilotShadowState(auth) {
  return getJson("/console/pilot/shadow-state", auth, "Pilot shadow state");
}

export async function getPilotAlerts(auth) {
  return getJson("/console/pilot/alerts", auth, "Pilot alerts");
}

export async function getPilotSystemStatus(auth) {
  return getJson("/console/pilot/system", auth, "Pilot system status");
}

export async function getPilotAuditTrail(auth) {
  return getJson("/console/pilot/audit", auth, "Pilot audit trail");
}

export async function getPilotOperationsQueue(auth) {
  return getJson("/console/pilot/queue", auth, "Pilot operations queue");
}

export async function getPilotInvites(auth) {
  return getJson("/console/pilot/invites", auth, "Pilot invites");
}